import {
  ALL_FILTER_VALUE,
  type Filter,
  type FilterOptionsDictionary,
  type FilterValue,
  type FilterValueDictionary,
  type RegionalOverviewFilterType
} from '@features/regional-overview/regional-overview.models';
import type { Feature, Geometry } from 'geojson';
import i18n from 'i18next';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { areFeatureListsEqual, areFilterValuesEqual, computeFilteredFeatures, deriveFiltersState } from './filters.utils';

const isFilterValueActive = (value: FilterValue): boolean => {
  if (value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0 && value[0] !== ALL_FILTER_VALUE;
  if (value === ALL_FILTER_VALUE) return false;
  return true;
};

interface FiltersState {
  isExpanded: boolean;
  isMobileOpen: boolean;
  enabledFilters: Filter[];
  features: Feature<Geometry, unknown>[];
  context: Record<string, unknown>;
  options: FilterOptionsDictionary;
  values: FilterValueDictionary;
  appliedValues: FilterValueDictionary;
  filteredFeatures: Feature<Geometry, unknown>[];

  toggleExpanded: () => void;
  resetExpanded: () => void;
  hasActiveFilters: () => boolean;
  setEnabledFilters: (enabledFilters: Filter[]) => void;
  setFeatures: (features: Feature<Geometry, unknown>[]) => void;
  setContext: (partial: Record<string, unknown>) => void;
  setValue: (filterType: RegionalOverviewFilterType, value: FilterValue) => void;
  applyValue: (filterType: RegionalOverviewFilterType, value: FilterValue) => void;
  applyValues: () => void;
  cancelValues: () => void;
  resetValues: () => void;
  resetStore: () => void;
  openMobile: () => void;
  closeMobile: () => void;
  closeFilters: () => void;
}

const INITIAL_STATE = {
  isExpanded: false,
  enabledFilters: [] as Filter[],
  features: [] as Feature<Geometry, unknown>[],
  context: {} as Record<string, unknown>,
  options: {} as FilterOptionsDictionary,
  values: {} as FilterValueDictionary,
  appliedValues: {} as FilterValueDictionary,
  filteredFeatures: [] as Feature<Geometry, unknown>[],
  isMobileOpen: false
};

export const useFiltersStore = create<FiltersState>()(
  subscribeWithSelector((set, get) => ({
    ...INITIAL_STATE,

    toggleExpanded: () =>
      set(state => (state.isExpanded ? { isExpanded: false, values: { ...state.appliedValues } } : { isExpanded: true })),
    resetExpanded: () => set({ isExpanded: false }),

    hasActiveFilters: () => Object.values(get().appliedValues).some(isFilterValueActive),

    setEnabledFilters: enabledFilters => {
      set({ enabledFilters });
    },

    setFeatures: features => {
      set({ features });
    },

    setContext: partial => {
      set(state => ({ context: { ...state.context, ...partial } }));
    },

    setValue: (filterType, value) => {
      set(state => ({ values: { ...state.values, [filterType]: value } }));
    },

    applyValue: (filterType, value) => {
      set(state => ({
        values: { ...state.values, [filterType]: value },
        appliedValues: { ...state.appliedValues, [filterType]: value }
      }));
    },

    applyValues: () => {
      set(state => (areFilterValuesEqual(state.values, state.appliedValues) ? {} : { appliedValues: { ...state.values } }));
    },

    cancelValues: () => {
      set(state => ({ values: { ...state.appliedValues } }));
    },

    resetValues: () => {
      set(state => {
        const defaults: FilterValueDictionary = {};
        state.enabledFilters.forEach(filter => {
          if (filter.defaultValue !== undefined) defaults[filter.type] = filter.defaultValue;
        });
        return { values: defaults };
      });
    },

    resetStore: () => set({ ...INITIAL_STATE }),

    openMobile: () => set({ isMobileOpen: true }),
    closeMobile: () => set(state => ({ isMobileOpen: false, values: { ...state.appliedValues } })),
    closeFilters: () => set({ isExpanded: false, isMobileOpen: false })
  }))
);

useFiltersStore.subscribe(
  state => ({ enabledFilters: state.enabledFilters, features: state.features, context: state.context }),
  (current, previous) => {
    const filtersChanged = current.enabledFilters !== previous.enabledFilters;
    const { values, appliedValues } = useFiltersStore.getState();
    const baseValues = filtersChanged ? {} : values;
    const baseApplied = filtersChanged ? {} : appliedValues;

    const derived = deriveFiltersState({
      enabledFilters: current.enabledFilters,
      features: current.features,
      context: current.context,
      values: baseValues,
      appliedValues: baseApplied
    });

    useFiltersStore.setState({
      options: derived.options,
      values: derived.values,
      appliedValues: derived.appliedValues
    });
  },
  {
    equalityFn: (a, b) => a.enabledFilters === b.enabledFilters && a.features === b.features && a.context === b.context
  }
);

i18n.on('languageChanged', () => {
  const { enabledFilters, features, context, values, appliedValues } = useFiltersStore.getState();
  const derived = deriveFiltersState({ enabledFilters, features, context, values, appliedValues });
  useFiltersStore.setState({ options: derived.options, values: derived.values, appliedValues: derived.appliedValues });
});

useFiltersStore.subscribe(
  state => ({
    enabledFilters: state.enabledFilters,
    features: state.features,
    context: state.context,
    appliedValues: state.appliedValues
  }),
  ({ enabledFilters, features, context, appliedValues }) => {
    const filteredFeatures = computeFilteredFeatures({ enabledFilters, features, context, appliedValues });
    if (!areFeatureListsEqual(filteredFeatures, useFiltersStore.getState().filteredFeatures)) {
      useFiltersStore.setState({ filteredFeatures });
    }
  },
  {
    equalityFn: (a, b) =>
      a.enabledFilters === b.enabledFilters && a.features === b.features && a.context === b.context && a.appliedValues === b.appliedValues
  }
);
